/**
 * Shop.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 * Updated: Jul 2026 — stronger SEO title/H1 + CollectionPage ItemList
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductGrid from './ProductGrid';
import SEO from './SEO';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Shop.css';

const BASE_URL = 'https://www.cranfordpub.ca';

const Shop = () => {
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const shop = t.shop;
  const userIsAdmin = isAdmin();
  const [searchParams] = useSearchParams();
  const hasSearchQuery = Boolean(searchParams.get('search') || searchParams.get('q'));
  const [catalogProducts, setCatalogProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/products')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (cancelled) return;
        setCatalogProducts(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCatalogProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const jsonLd = useMemo(() => {
    const items = catalogProducts.slice(0, 40).map((p, index) => {
      const slug = p.permalink || p.id;
      const url = `${BASE_URL}/product/${slug}`;
      const image = p.image
        ? (p.image.startsWith('http') ? p.image : `${BASE_URL}${p.image}`)
        : undefined;
      const listItem = {
        '@type': 'ListItem',
        position: index + 1,
        url,
        name: p.name,
      };
      if (image) listItem.image = image;
      if (p.price_cents != null) {
        listItem.offers = {
          '@type': 'Offer',
          priceCurrency: 'CAD',
          price: (Number(p.price_cents) / 100).toFixed(2),
          availability: 'https://schema.org/InStock',
          url,
        };
      }
      return listItem;
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: shop.h1,
      description: shop.pageDescription,
      url: `${BASE_URL}/shop`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Cranford Publications',
        url: BASE_URL,
      },
      mainEntity: {
        '@type': 'ItemList',
        name: shop.itemListName,
        numberOfItems: catalogProducts.length || items.length,
        itemListElement: items,
      },
    };
  }, [catalogProducts, shop.h1, shop.pageDescription, shop.itemListName]);

  return (
    <>
      <SEO
        title={shop.pageTitle}
        description={shop.pageDescription}
        canonical="/shop"
        robots={hasSearchQuery ? 'noindex, follow' : 'index, follow'}
        keywords={shop.keywords}
        jsonLd={jsonLd}
      />
      <div className="shop-container">
        <h1 className="shop-title">{shop.h1}</h1>

        <ProductGrid
          adminMode={userIsAdmin}
          useAuthHeaders={userIsAdmin}
          productsUrl={userIsAdmin ? '/api/admin/products' : undefined}
          heading={shop.collectionHeading}
        />
      </div>
    </>
  );
};

export default Shop;
